;(function (angular) {
   'use strict'

   angular.module('azure.data').factory('productData', productData)

   function productData($q, AzureAPI, AzureAlgolia, config, util, CacheFactory) {
      //================================================================================
      // Cache
      //================================================================================

      const _cache = CacheFactory.createCache('products')
      const _packagedProductCache = CacheFactory.createCache('packagedProducts', {
         maxAge: 30 * 60 * 1000, // 30 minutes
      })

      // cache key variables, used for building cache keys (each should be unique)
      const _keyProduct = 0
      const _keyProductByCode = 1
      const _keyProductByGtin13 = 2
      const _keyPackagedProduct = 3
      const _keyPackagedProductsByCodePromise = 4

      const getCacheKey = util.joinArgs
      const retryOnce = util.asyncRetryOnce

      function cacheProduct(product, byCode, full) {
         if (!product) {
            return
         }
         _cache.put(getCacheKey(_keyProduct, product.id, full), product)
         if (byCode) {
            product.packaging.forEach(function (packaging) {
               _cache.put(getCacheKey(_keyProductByCode, packaging.code), product)
            })
         }
         return product
      }

      function cacheProducts(products, byCode, full) {
         return products.map(function (product) {
            return cacheProduct(product, byCode, full)
         })
      }

      function replacePackagedProduct(product, packagedProduct) {
         const index = util.findIndexByPropertyValue(product.packaging, 'code', packagedProduct.code)
         if (index >= 0) {
            product.packaging[index] = packagedProduct
         }
      }

      function cachePackagedProduct(packagedProduct) {
         _packagedProductCache.put(getCacheKey(_keyPackagedProduct, packagedProduct.code), packagedProduct)
         const productByCodeCacheKey = getCacheKey(_keyProductByCode, packagedProduct.code)
         const cachedProductByCode = _cache.get(productByCodeCacheKey)
         if (cachedProductByCode) {
            replacePackagedProduct(cachedProductByCode, packagedProduct)
         }
         const cachedProduct =
            _cache.get(getCacheKey(_keyProduct, packagedProduct.product, false)) ||
            _cache.get(getCacheKey(_keyProduct, packagedProduct.product, true))
         if (cachedProduct) {
            replacePackagedProduct(cachedProduct, packagedProduct)
         }
      }

      function cachePackagedProducts(packagedProducts) {
         packagedProducts.forEach(cachePackagedProduct)
         return packagedProducts
      }

      //================================================================================
      // Sort By
      //================================================================================

      const _sortBy = Object.freeze({
         name: 'name',
         brand: 'brand',
         favorites: 'favorites',
         relevance: 'relevance',
         orderFrequency: 'orderFrequency',
         orderRecency: 'orderRecency',
      })

      const _productCodes = Object.freeze({
         salesFlyer: 'CT005',
      })

      //================================================================================
      // Algolia Index Setup
      //================================================================================

      /**
       * Helper function to create product index configurations
       * @param {boolean} cacheSearches - Whether to enable search caching
       * @returns {Object} Object containing productIndex and productIndexes
       */
      function createProductIndexes(cacheSearches) {
         const cacheOptions = {cacheObjects: false, cacheSearches: cacheSearches}

         const productIndex = AzureAlgolia.createIndexProxy(config.algoliaIndexNames.products, cacheOptions)

         const productIndexes = {}
         productIndexes[_sortBy.name] = AzureAlgolia.createIndexProxy(
            config.algoliaIndexNames.productsByName,
            cacheOptions
         )
         productIndexes[_sortBy.brand] = AzureAlgolia.createIndexProxy(
            config.algoliaIndexNames.productsByBrand,
            cacheOptions
         )
         productIndexes[_sortBy.favorites] = AzureAlgolia.createIndexProxy(
            config.algoliaIndexNames.productsByFavoritesDesc,
            cacheOptions
         )
         productIndexes[_sortBy.relevance] = productIndex
         productIndexes[_sortBy.orderFrequency] = productIndex
         productIndexes[_sortBy.orderRecency] = productIndex

         return {productIndex, productIndexes}
      }

      const {productIndex: _productIndex, productIndexes: _productIndexes} = createProductIndexes(true)

      const {productIndex: _noCacheProductIndex, productIndexes: _noCacheProductIndexes} = createProductIndexes(false)

      // The inline options when getting products by id from the API
      const _apiInline = 'brand,packaging.favorites,packaging.next-purchase-arrival,substitutions,favorites'

      const _apiInlineFull =
         'brand,packaging.favorites,packaging.next-purchase-arrival,substitutions,favorites,description,links'

      // The default product attributes to retrieve when getting products from Algolia
      const _defaultAlgoliaAttributes = [
         'id',
         'brand.name',
         'name',
         'substitutions',
         'favorites',
         'storageClimate',
         'slug',
         'isShippableUps',
         'maxStorageDays',
         'treatAsActive',
         'unshippableRegions',
         'packaging.code',
         'packaging.price',
         'packaging.weight',
         'packaging.volume',
         'packaging.tags',
         'packaging.images',
         'packaging.size',
         'packaging.stock',
         'packaging.next-purchase-arrival',
         'packaging.favorites',
         'packaging.bargain-bin-notes',
         'packaging.rewardsEnabled',
         'packaging.trustpilotNumberOfReviews',
         'packaging.trustpilotStarsAverage',
         'packaging.freightHandlingRequired',
         'packaging.vendorShortedLastPurchase',
         'packaging.primary-category',
      ]

      // The product attributes to retrieve when getting products by gtin13 from Algolia
      const _byGtin13AlgoliaAttributes = ['id', 'brand.name', 'name', 'slug', 'packaging.code', 'packaging.gtin13']

      //================================================================================
      // Product Query Regular Expressions
      //================================================================================

      // Support gtin12 or gtin13 searches
      const _gtinRegex = /^\d{12,13}$/
      const _codeRegex = /^([a-z]{2}\d{3,}|gift\d{2,}|[a-c|e-z][a-z]{2,}\d{3,})$/i
      const _idRegex = /^\d{4,11}$/

      //================================================================================
      // Products
      //================================================================================

      const _stockStatuses = Object.freeze({
         inStock: 'inStock',
         outOfStock: 'outOfStock',
         arrivingAfterOrder: 'arrivingAfterOrder',
         arrivingBeforeOrder: 'arrivingBeforeOrder',
         vendorOutOfStockUpcomingOrder: 'vendorOutOfStockUpcomingOrder',
         vendorOutOfStockNoUpcomingOrder: 'vendorOutOfStockNoUpcomingOrder',
      })

      function findPackagingByCode(product, code) {
         return product.packaging.find(function (packagedProduct) {
            return packagedProduct.code === code
         })
      }

      function productIfMatchingCode(product, code) {
         if (!product) {
            return
         }

         if (!code || findPackagingByCode(product, code)) {
            return product
         }
      }

      function getProductFromApi(productIdAndCode, full) {
         if (!productIdAndCode) {
            return $q.resolve()
         }

         full = !!full

         return retryOnce(AzureAPI.product.get, {
            id: productIdAndCode.id,
            inline: full ? _apiInlineFull : _apiInline,
         })
            .then(util.cleanItem)
            .then(function (product) {
               cacheProduct(product, false, full)
               return productIfMatchingCode(product, productIdAndCode.code)
            })
      }

      function getProductFromAlgolia(productIdAndCode, full) {
         const attributes = full ? undefined : _defaultAlgoliaAttributes
         return _productIndex.getObject(productIdAndCode.id, attributes).then(function (product) {
            cacheProduct(product, false, full)
            return productIfMatchingCode(product, productIdAndCode.code)
         })
      }

      function getProduct(productIdAndCode, full) {
         if (!productIdAndCode) {
            return $q.resolve()
         }

         full = !!full

         const cacheKey = getCacheKey(_keyProduct, productIdAndCode.id, full)
         let product = productIfMatchingCode(_cache.get(cacheKey), productIdAndCode.code)
         if (!product && !full) {
            product = productIfMatchingCode(
               _cache.get(getCacheKey(_keyProduct, productIdAndCode.id, true)),
               productIdAndCode.code
            )
         }

         if (!product) {
            product = getProductFromAlgolia(productIdAndCode, full).then(function (product) {
               return product || getProductFromApi(productIdAndCode, full)
            })
         }

         return $q.resolve(product)
      }

      function getProductsFromApi(productIdsAndCodes) {
         const productIds = util.mapToIds(productIdsAndCodes)
         const codes = util.mapToProperty('code', productIdsAndCodes)
         return retryOnce(AzureAPI.product.query, {
            id: productIds.join(),
            'packaged-product': codes.join(),
            inline: _apiInline,
            limit: config.apiLimit,
         }).then(function (products) {
            cacheProducts(products, false, false)
            return getProductsByIdDictionary(productIdsAndCodes, products)
         })
      }

      function getProductsFromAlgolia(productIdsAndCodes) {
         const productIds = util.mapToIds(productIdsAndCodes)
         return _productIndex.getObjects(productIds, _defaultAlgoliaAttributes).then(function (products) {
            cacheProducts(products, false, false)
            return getProductsByIdDictionary(productIdsAndCodes, products)
         })
      }

      function getProducts(productIdsAndCodes, noApiFallback) {
         const cacheResult = {
            notFound: [],
            productsById: {},
         }
         productIdsAndCodes.forEach(function (idAndCode) {
            let product =
               _cache.get(getCacheKey(_keyProduct, idAndCode.id, false)) ||
               _cache.get(getCacheKey(_keyProduct, idAndCode.id, true))

            product = productIfMatchingCode(product, idAndCode.code)
            if (product) {
               cacheResult.productsById[product.id] = product
            } else if (idAndCode.id) {
               cacheResult.notFound.push(idAndCode)
            }
         })

         if (cacheResult.notFound.length === 0) {
            return $q.resolve(cacheResult)
         }

         const cacheAndAlgoliaResultPromise = getProductsFromAlgolia(cacheResult.notFound).then(function (
            algoliaResult
         ) {
            return {
               notFound: algoliaResult.notFound,
               productsById: angular.extend(cacheResult.productsById, algoliaResult.productsById),
            }
         })

         return cacheAndAlgoliaResultPromise.then(function (cacheAndAlgoliaResult) {
            if (cacheAndAlgoliaResult.notFound.length === 0 || noApiFallback) {
               return cacheAndAlgoliaResult
            }
            return getProductsFromApi(cacheAndAlgoliaResult.notFound).then(function (apiResult) {
               return {
                  notFound: apiResult.notFound,
                  productsById: angular.extend(cacheAndAlgoliaResult.productsById, apiResult.productsById),
               }
            })
         })
      }

      function checkAlgolia(productIdsAndCodes, priceLevel) {
         const productIds = util.mapToIds(productIdsAndCodes)
         return _noCacheProductIndex
            .getObjects(productIds, ['id,packaging.code,packaging.price'])
            .then(function (products) {
               const found = []
               const notFound = []
               productIdsAndCodes.forEach(function (productIdAndCode) {
                  const productId = Number(productIdAndCode.id)
                  const product = util.findById(products, productId)
                  const pack = product && findPackagingByCode(product, productIdAndCode.code)
                  if (pack && pack.price[priceLevel]) {
                     found.push(productIdAndCode)
                  } else {
                     notFound.push(productIdAndCode)
                  }
               })
               return {
                  found: found,
                  notFound: notFound,
               }
            })
      }

      function searchProducts({
         query,
         searchOptions,
         sortBy = _sortBy.relevance,
         shouldCacheProducts = true,
         shouldUseCachedIndex = true,
      }) {
         const indexToSearch = shouldUseCachedIndex ? _productIndexes[sortBy] : _noCacheProductIndexes[sortBy]

         return indexToSearch.search(query, searchOptions).then(function (response) {
            if (shouldCacheProducts) {
               cacheProducts(response.hits, false, false)
            }
            return response
         })
      }

      function findProductByCode(products, code) {
         return products.find(function (product) {
            return productIfMatchingCode(product, code)
         })
      }

      function getProductsByIdDictionary(productIdsAndCodes, products) {
         const productsById = {}
         const notFound = []
         productIdsAndCodes.forEach(function (idAndCode) {
            const productId = Number(idAndCode.id)
            const product = productIfMatchingCode(util.findById(products, productId), idAndCode.code)
            if (product) {
               productsById[productId] = product
            } else {
               notFound.push(idAndCode)
            }
         })
         return {
            productsById: productsById,
            notFound: notFound,
         }
      }

      function getProductsByCodeDictionary(codes, products) {
         const productsByCode = {}
         const notFound = []
         codes.forEach(function (code) {
            const product = findProductByCode(products, code)
            if (product) {
               productsByCode[code] = product
            } else {
               notFound.push(code)
            }
         })
         return {
            productsByCode: productsByCode,
            notFound: notFound,
         }
      }

      function getPackagedProductsByCodeDictionary(codes, packagedProducts) {
         const packagedProductsByCode = {}
         const notFound = []
         codes.forEach(function (code) {
            const packagedProduct = util.findByPropertyValue(packagedProducts, 'code', code)
            if (packagedProduct) {
               packagedProductsByCode[code] = packagedProduct
            } else {
               notFound.push(code)
            }
         })
         return {
            packagedProductsByCode: packagedProductsByCode,
            notFound: notFound,
         }
      }

      function getProductsByCodeFromApi(codes) {
         return retryOnce(AzureAPI.product.query, {
            'packaged-product': codes.join(),
            inline: _apiInline,
            limit: config.apiLimit,
         }).then(function (products) {
            cacheProducts(products, true, false)
            return getProductsByCodeDictionary(codes, products)
         })
      }

      function getProductsByCodeFromAlgolia(codes) {
         const algoliaParameters = {
            hitsPerPage: codes.length,
            attributesToRetrieve: _defaultAlgoliaAttributes.join(),
            attributesToHighlight: '',
            filters: codes
               .map(function (code) {
                  return 'packaging.code:' + code
               })
               .join(' OR '),
         }
         return _noCacheProductIndex.search(algoliaParameters).then(function (response) {
            const products = response.hits
            cacheProducts(products, true, false)
            return getProductsByCodeDictionary(codes, products)
         })
      }

      function getProductsByCode(codes, skipAlgolia) {
         const cacheResult = {
            notFound: [],
            productsByCode: {},
         }

         codes.forEach(function (code) {
            const product = _cache.get(getCacheKey(_keyProductByCode, code))
            if (product) {
               cacheResult.productsByCode[code] = product
            } else {
               cacheResult.notFound.push(code)
            }
         })

         const cacheResultPromise = $q.resolve(cacheResult)

         if (cacheResult.notFound.length === 0) {
            return cacheResultPromise
         }

         let cacheAndAlgoliaResultPromise
         if (skipAlgolia) {
            cacheAndAlgoliaResultPromise = cacheResultPromise
         } else {
            cacheAndAlgoliaResultPromise = getProductsByCodeFromAlgolia(cacheResult.notFound).then(function (
               algoliaResult
            ) {
               return {
                  notFound: algoliaResult.notFound,
                  productsByCode: angular.extend(cacheResult.productsByCode, algoliaResult.productsByCode),
               }
            })
         }

         return cacheAndAlgoliaResultPromise.then(function (cacheAndAlgoliaResult) {
            if (cacheAndAlgoliaResult.notFound.length === 0) {
               return cacheAndAlgoliaResult
            }
            return getProductsByCodeFromApi(cacheAndAlgoliaResult.notFound).then(function (apiResult) {
               return {
                  notFound: apiResult.notFound,
                  productsByCode: angular.extend(cacheAndAlgoliaResult.productsByCode, apiResult.productsByCode),
               }
            })
         })
      }

      function getProductByCode(code, skipAlgolia) {
         return getProductsByCode([code], skipAlgolia).then(function (result) {
            return result.productsByCode[code]
         })
      }

      function getProductByGtin13(gtin13) {
         // TODO: add fall back to API
         const cacheKey = getCacheKey(_keyProductByGtin13, gtin13)
         let product = _cache.get(cacheKey)
         if (!product) {
            const algoliaParameters = {
               hitsPerPage: 1,
               attributesToRetrieve: _byGtin13AlgoliaAttributes.join(),
               attributesToHighlight: '',
               filters: 'packaging.gtin13:' + gtin13,
            }
            product = _noCacheProductIndex.search(algoliaParameters).then(function (response) {
               const products = response.hits
               if (products.length) {
                  return products[0]
               }
            })

            // cache the promise, which is replaced with the resolved value by CacheFactory option storeOnResolve
            _cache.put(cacheKey, product)
         }

         // $q.resolve ensures that this always returns a promise that resolves with the expected object
         return $q.resolve(product)
      }

      function getProductByQuery(query) {
         let maybeProductPromise

         if (_idRegex.test(query)) {
            maybeProductPromise = getProduct({id: query})
         } else if (_codeRegex.test(query)) {
            const code = query.toUpperCase()
            maybeProductPromise = getProductByCode(code).then(function (product) {
               if (product) {
                  product.codes = [code]
               }
               return product
            })
         } else if (_gtinRegex.test(query)) {
            const gtin13 = query.length === 12 ? '0' + query : query
            maybeProductPromise = getProductByGtin13(gtin13).then(function (product) {
               if (product) {
                  const packaging = product.packaging.filter(function (packagedProduct) {
                     return packagedProduct.gtin13 === gtin13
                  })
                  product.codes = util.mapToProperty('code', packaging)
               }
               return product
            })
         }

         return $q.resolve(maybeProductPromise)
      }

      function getPackagedProductByOldId(id) {
         return retryOnce(AzureAPI['packaged-product'].old, {
            id: id,
         }).then(util.cleanItem)
      }

      function getPackagedProductsByCodeFromApi(codes) {
         const joinedCodes = codes.join()
         const cacheKey = getCacheKey(_keyPackagedProductsByCodePromise, joinedCodes)
         let promise = _cache.get(cacheKey)
         if (!promise) {
            promise = retryOnce(AzureAPI['packaged-product'].query, {
               code: joinedCodes,
               inline: 'next-purchase-arrival,favorites',
               limit: config.apiLimit,
            })
            _cache.put(cacheKey, promise)
         }

         return promise.then(cachePackagedProducts).then(function (packagedProducts) {
            _cache.remove(cacheKey)
            return getPackagedProductsByCodeDictionary(codes, packagedProducts)
         })
      }

      function getPackagedProductsByCode(codes) {
         const cacheResult = {
            notFound: [],
            packagedProductsByCode: {},
         }

         codes.forEach(function (code) {
            const packagedProduct = _packagedProductCache.get(getCacheKey(_keyPackagedProduct, code))
            if (packagedProduct) {
               cacheResult.packagedProductsByCode[code] = packagedProduct
            } else {
               cacheResult.notFound.push(code)
            }
         })

         if (cacheResult.notFound.length === 0) {
            return $q.resolve(cacheResult)
         }

         return getPackagedProductsByCodeFromApi(cacheResult.notFound).then(function (apiResult) {
            return {
               notFound: apiResult.notFound,
               packagedProductsByCode: angular.extend(
                  cacheResult.packagedProductsByCode,
                  apiResult.packagedProductsByCode
               ),
            }
         })
      }

      function getAlgoliaStockFilters(pickDate) {
         let inStockFilters = 'packaging.stock > 0'
         let outOfStockFilters = 'packaging.stock = 0'
         if (pickDate) {
            const pickDateTimestamp = Math.round(pickDate.getTime() / 1000)
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const arrivalWindowStart = new Date(today)
            arrivalWindowStart.setDate(arrivalWindowStart.getDate() - config.purchaseArrivalLateWindowInDays)
            const arrivalWindowStartTimestamp = Math.round(arrivalWindowStart.getTime() / 1000)
            const todayTimestamp = Math.round(today.getTime() / 1000)
            const arrivalWindowEndTimestamp = Math.max(pickDateTimestamp, todayTimestamp)
            const arrivalFilter =
               'packaging.next-purchase-arrival-timestamp: ' +
               arrivalWindowStartTimestamp +
               ' TO ' +
               arrivalWindowEndTimestamp
            inStockFilters = '(' + inStockFilters + ' OR ' + arrivalFilter + ')'
            outOfStockFilters += ' AND NOT ' + arrivalFilter
         }
         return {
            inStock: inStockFilters,
            outOfStock: outOfStockFilters,
         }
      }

      //================================================================================

      return {
         algoliaPaginationLimitedTo: 2000, // This matches a setting on the algolia product indices
         defaultAlgoliaAttributes: _defaultAlgoliaAttributes,

         checkAlgolia: checkAlgolia,
         getProduct: getProduct,
         getProducts: getProducts,
         searchProducts: searchProducts,
         getProductsByCode: getProductsByCode,
         getProductByCode: getProductByCode,
         getProductByQuery: getProductByQuery,
         getPackagedProductByOldId: getPackagedProductByOldId,
         getPackagedProductsByCode: getPackagedProductsByCode,
         getAlgoliaStockFilters: getAlgoliaStockFilters,

         // Sort By:
         sortBy: _sortBy,

         // Product Codes
         productCodes: _productCodes,

         // Stock statuses
         stockStatuses: _stockStatuses,
      }
   }
})(angular)
